import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  forwardRef,
  Input,
  Output,
  ViewChild,
} from '@angular/core';

import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import {
  ControlValueAccessor,
  FormControl,
  FormGroup,
  FormsModule,
  NG_VALUE_ACCESSOR,
  ReactiveFormsModule,
} from '@angular/forms';
import { DateHelperService } from '../services/date-helper.service';
import { DateTime } from 'luxon';
import { InfoTooltipComponent } from './info-tooltip.component';

@Component({
  selector: 'app-datetime-input',
  imports: [
    MatDatepickerModule,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
    FormsModule,
    InfoTooltipComponent,
  ],
  template: `
    <div class="flex w-full gap-4">
      @if (!hideDate) {
        <!-- dd-compact-field opts these two inner fields into the compact Figma geometry. -->
        <mat-form-field class="dd-compact-field flex-1" [class.mat-form-field-invalid]="required && !datePart">
          <mat-label
            >{{ label }}
            <app-info-tooltip [tooltip]="info"></app-info-tooltip>
          </mat-label>
          <input
            matInput
            #dateInput
            #date="ngModel"
            name="date"
            [readonly]="isEmbedded"
            [disabled]="isDisabled"
            [required]="required"
            [ngModelOptions]="{ standalone: true }"
            data-cy="datetime-input-date"
            [(ngModel)]="datePart"
            (ngModelChange)="onDateChange($event)"
            [matDatepicker]="picker"
            (click)="openPicker(dateInput, picker)" />
          <mat-hint>DD-MM-YYYY</mat-hint>
          <mat-datepicker-toggle matIconSuffix [for]="picker"></mat-datepicker-toggle>
          <mat-datepicker #picker [startAt]="startDate"></mat-datepicker>
          @if (date.errors) {
            <mat-error>Voer een geldige datum in. </mat-error>
          }
        </mat-form-field>
      }
      <mat-form-field
        class="dd-compact-field flex-1 min-w-[73px] pb-2"
        [class.max-w-32]="!hideDate"
        [class.mat-form-field-invalid]="required && !timePart && !isDisabled">
        @if (!hideTimelabel) {
          <mat-label>{{ hideDate ? label : 'Tijd' }}</mat-label>
        }
        <input
          type="time"
          step="900"
          matInput
          [class.dd-time-empty]="!timePart"
          [(ngModel)]="timePart"
          [required]="isDisabled ? false : required"
          [ngModelOptions]="{ standalone: true }"
          (ngModelChange)="onTimeChange($event)"
          data-cy="datetime-input-time"
          #timeInput
          #time="ngModel"
          name="time"
          class="text-center"
          [disabled]="isDisabled" />
        <span matSuffix class="dd-clear-suffix">
          <ng-content select="[datetimeTrailingAction]"></ng-content>
        </span>
        @if (time.errors) {
          <mat-error>Voer een geldige tijd in. </mat-error>
        }
      </mat-form-field>
    </div>
  `,
  styles: ``,
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimeInputComponent),
      multi: true,
    },
  ],
})
export class DatetimeInputComponent implements ControlValueAccessor {
  @ViewChild('dateInput') private dateInput?: ElementRef<HTMLInputElement>;
  @ViewChild('timeInput') private timeInput?: ElementRef<HTMLInputElement>;

  @Input() label: string = 'Datum';
  @Input() hideTimelabel = false;
  @Input() hideDate = false;
  @Input() disabled = false;
  @Input() required = false;
  @Input() info: string;
  isEmbedded = !!window.flutter_inappwebview;

  formGroup = new FormGroup({
    date: new FormControl(''),
    time: new FormControl(''),
  });

  @Input() set dateTime(value: string | null) {
    if (value !== this._dateTime) {
      this._dateTime = value ?? '';
      this.parseDateTime(value);
    }
  }

  get dateTime(): string {
    return this._dateTime;
  }

  @Input() isDisabled = false;
  @Output() dateTimeChange = new EventEmitter<string>();

  private _dateTime = '';

  private onTouchedCallback = () => {};
  private onChangeCallback: (_: any) => void = () => {};

  datePart = null;
  timePart = null;
  @Input() startDate;

  writeValue(value: any) {
    this.dateTime = value;
  }

  registerOnChange(fn: any) {
    this.onChangeCallback = fn;
  }

  registerOnTouched(fn: any) {
    this.onTouchedCallback = fn;
  }

  private parseDateTime(value: string | null) {
    if (value === null || !value) {
      this.datePart = null;
      this.timePart = null;
      return;
    }

    const { date, time } = DateHelperService.dateTimeToDateAndTime(value);
    this.datePart = date;
    this.timePart = time;
  }

  onDateChange(date: DateTime) {
    this.updateDateTime(date?.toISODate() ?? null, this.timePart);
  }

  onTimeChange(time: string) {
    this.updateDateTime(this.datePart, time);
  }

  private updateDateTime(date: string, time: string) {
    // Time-only fields (hideDate) have no date input, so `date` arrives as null and
    // `dateAndTimeToDateTime` would drop the entered time. Fall back to the date the
    // control already holds, or today, so the time still produces a complete value.
    if (this.hideDate && date == null) {
      const existing = DateHelperService.dateTimeToDateAndTime(this._dateTime);
      date = existing.date ?? DateHelperService.dateObjectToDateString(new Date());
    }

    const newTimeDate = DateHelperService.dateAndTimeToDateTime(date, time);
    if (newTimeDate) {
      this._dateTime = newTimeDate;
      this.onChangeCallback(this._dateTime);
      this.dateTimeChange.emit(this._dateTime);
      this.onTouchedCallback();
    }
  }

  focus(): void {
    (this.hideDate ? this.timeInput : this.dateInput)?.nativeElement.focus();
  }

  // Open picker while maintaining input focus
  openPicker(input: HTMLInputElement, picker: any): void {
    picker.open();
    // Maintain focus on input after picker opens
    setTimeout(() => input.focus(), 32);
  }
}
